import React from "react";
import {
  Activity,
  AlarmClock,
  Anchor,
  BatteryCharging,
  Bike,
  Cable,
  Cake,
  CalendarCheck,
  CalendarCheck2,
  CalendarClock,
  CalendarDays,
  CalendarHeart,
  CalendarRange,
  Cat,
  CircleHelp,
  ClipboardCheck,
  Clock,
  CloudSunRain,
  Copy,
  Dumbbell,
  Flag,
  Flame,
  Flower2,
  Gauge,
  Ghost,
  Gift,
  Globe,
  Handshake,
  HeartHandshake,
  HeartPulse,
  Hourglass,
  Layers,
  Leaf,
  Moon,
  MoonStar,
  Mountain,
  Package,
  PartyPopper,
  Pi,
  Rabbit,
  Radio,
  RotateCcw,
  Route,
  Skull,
  Ship,
  Snowflake,
  Sparkles,
  Sun,
  Sprout,
  SunMoon,
  Sunrise,
  Telescope,
  ThermometerSnowflake,
  ThermometerSun,
  Timer,
  TreePine,
  Trophy,
  Users,
  UsersRound,
  Utensils,
  Waves,
  Wind,
  Zap,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { medalShape, shortTier } from "./badgeDisplay";

// The badge medal (#170): one flat SVG, no image files. The frame's shape says
// the category, its colour the rank, a ribbon carries the tier number and the
// icon is from Lucide like the rest of the app. A locked medal is grey, with
// its progress drawn around the rim.

// Icon names come from the server's catalogue (api/lib/badges.js).
const ICONS = {
  Activity,
  AlarmClock,
  Anchor,
  BatteryCharging,
  Bike,
  Cable,
  Cake,
  CalendarCheck,
  CalendarCheck2,
  CalendarClock,
  CalendarDays,
  CalendarHeart,
  CalendarRange,
  Cat,
  CircleHelp,
  ClipboardCheck,
  Clock,
  CloudSunRain,
  Copy,
  Dumbbell,
  Flag,
  Flame,
  Flower2,
  Gauge,
  Ghost,
  Gift,
  Globe,
  Handshake,
  HeartHandshake,
  HeartPulse,
  Hourglass,
  Layers,
  Leaf,
  Moon,
  MoonStar,
  Mountain,
  Package,
  PartyPopper,
  Pi,
  Rabbit,
  Radio,
  RotateCcw,
  Route,
  Skull,
  Ship,
  Snowflake,
  Sparkles,
  Sun,
  Sprout,
  SunMoon,
  Sunrise,
  Telescope,
  ThermometerSnowflake,
  ThermometerSun,
  Timer,
  TreePine,
  Trophy,
  Users,
  UsersRound,
  Utensils,
  Waves,
  Wind,
  Zap,
};

export const RANK_COLOURS = {
  bronze: "#D98A4E",
  silver: "#9EAAB8",
  gold: "#F5C542",
  platinum: "#8CC8FF",
  diamond: "#F4F7FB",
  special: "hsl(var(--primary))",
  secret: "#A78BFA",
};

const LOCKED = {
  rim: "hsl(var(--border))",
  fill: "hsl(var(--muted) / 0.35)",
  glyph: "hsl(var(--muted-foreground) / 0.5)",
  secretRim: "#3A3350",
  secretGlyph: "#5B5470",
};

// ---- shapes, each in a 100×100 box centred on 50,50 ----

const polygon = (n, r, rotation) =>
  Array.from({ length: n }, (_, i) => {
    const a = ((rotation + (360 / n) * i) * Math.PI) / 180;
    return [50 + r * Math.cos(a), 50 + r * Math.sin(a)];
  });

// Round each corner with a quadratic curve `k` of the way along its edges.
const rounded = (points, k) => {
  const n = points.length;
  const f = (v) => v.toFixed(2);
  let d = "";
  points.forEach(([cx, cy], i) => {
    const [px, py] = points[(i - 1 + n) % n];
    const [nx, ny] = points[(i + 1) % n];
    d += `${i ? "L" : "M"}${f(cx + (px - cx) * k)},${f(cy + (py - cy) * k)}`;
    d += `Q${f(cx)},${f(cy)} ${f(cx + (nx - cx) * k)},${f(cy + (ny - cy) * k)}`;
  });
  return `${d}Z`;
};

const rosette = () => {
  const n = 12;
  const points = Array.from({ length: n * 2 }, (_, i) => {
    const a = ((-90 + (180 / n) * i) * Math.PI) / 180;
    const r = i % 2 ? 40 : 46;
    return [50 + r * Math.cos(a), 50 + r * Math.sin(a)];
  });
  return rounded(points, 0.35);
};

const SHAPES = {
  circle: "M50,4A46,46 0 1 1 49.99,4Z",
  shield: "M50,4C62,10 76,12 90,12C90,46 84,74 50,96C16,74 10,46 10,12C24,12 38,10 50,4Z",
  hexagon: rounded(polygon(6, 47, -90), 0.14),
  octagon: rounded(polygon(8, 47, -112.5), 0.12),
  diamond: rounded(polygon(4, 48, -90), 0.2),
  square: rounded(
    [
      [8, 8],
      [92, 8],
      [92, 92],
      [8, 92],
    ],
    0.22,
  ),
  rosette: rosette(),
};

// Where each shape looks centred; a shield's weight sits high.
const CENTRE_Y = { shield: 46 };

const star = (x, y, r) =>
  `M${x},${y - r}Q${x},${y} ${x + r},${y}Q${x},${y} ${x},${y + r}Q${x},${y} ${x - r},${y}Q${x},${y} ${x},${y - r}Z`;
const SPARKLES = [
  [84, 14, 11, 0],
  [13, 27, 7.5, 0.9],
  [89, 63, 6.5, 1.7],
];

const SIZES = { sm: 32, md: 48, lg: 64 };

// An eight-point starburst, the "new" mark (#201): white with a ring in the
// page colour, no disc, so there's no red competing with the medal. Eight
// points so it can't be mistaken for a diamond's four-point sparkle.
const starburst = (x, y, outer, inner = outer * 0.5) =>
  `${Array.from({ length: 16 }, (_, i) => {
    const r = i % 2 ? inner : outer;
    const a = (Math.PI / 8) * i - Math.PI / 2;
    return `${i ? "L" : "M"}${(x + r * Math.cos(a)).toFixed(2)},${(y + r * Math.sin(a)).toFixed(2)}`;
  }).join("")}Z`;

// Bigger at the smallest size so it still reads at 32 px.
const NEW_MARK = { sm: 22, md: 16, lg: 14 };

/**
 * One medal. `badge` is a row from GET /api/badges. `pop` plays the
 * one-off "just earned" animation; `isNew` adds the new mark, a white
 * starburst top right that spins in once when the medal appears.
 */
export default function BadgeMedal({ badge, size = "md", pop = false, isNew = false, className }) {
  const px = SIZES[size] || SIZES.md;
  const earned = Boolean(badge.earned_at);
  const secret = badge.rank === "secret";
  const shape = medalShape(badge);
  const d = SHAPES[shape];
  const colour = RANK_COLOURS[badge.rank] || RANK_COLOURS.special;
  const Icon = ICONS[badge.icon] || Dumbbell;
  // The ribbon number is unreadable at the smallest size.
  const tier = size !== "sm" && badge.tier != null ? shortTier(badge.tier) : null;
  const iconSize = 38;
  const cy = (CENTRE_Y[shape] || 50) - (tier ? 5 : 0);
  const p = !earned && badge.progress ? badge.progress.current / badge.progress.target : null;

  const rim = earned ? colour : secret ? LOCKED.secretRim : LOCKED.rim;
  const glyph = earned ? colour : secret ? LOCKED.secretGlyph : LOCKED.glyph;
  const mark = NEW_MARK[size] || NEW_MARK.md; // the new mark's radius

  return (
    <svg
      viewBox="-3 -3 106 106"
      width={px}
      height={px}
      aria-hidden="true"
      className={cn("shrink-0 overflow-visible", pop && "motion-safe:animate-badge-pop", className)}
    >
      <path
        d={d}
        fill={earned ? colour : LOCKED.fill}
        fillOpacity={earned ? 0.14 : 1}
        stroke={rim}
        strokeWidth="4"
        strokeLinejoin="round"
      />
      {p != null && p > 0 && (
        <path
          d={d}
          pathLength="100"
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray={`${Math.min(100, p * 100).toFixed(1)} 100`}
        />
      )}
      <Icon
        x={50 - iconSize / 2}
        y={cy - iconSize / 2}
        width={iconSize}
        height={iconSize}
        color={glyph}
        strokeWidth={2}
      />
      {earned &&
        badge.rank === "diamond" &&
        SPARKLES.map(([x, y, r, delay]) => (
          <path
            key={`${x},${y}`}
            d={star(x, y, r)}
            fill="#FFFFFF"
            className="motion-safe:animate-badge-twinkle"
            style={{ transformBox: "fill-box", transformOrigin: "center", animationDelay: `${delay}s` }}
          />
        ))}
      {tier && (
        <g>
          <rect
            x={50 - Math.max(30, 12 + tier.length * 10) / 2}
            y="76"
            width={Math.max(30, 12 + tier.length * 10)}
            height="20"
            rx="6"
            fill={earned ? colour : "hsl(var(--muted))"}
            stroke="hsl(var(--background))"
            strokeWidth="3"
          />
          <text
            x="50"
            y="91"
            textAnchor="middle"
            fontSize="16"
            fontWeight="700"
            fill={earned ? "hsl(var(--background))" : "hsl(var(--muted-foreground))"}
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {tier}
          </text>
        </g>
      )}
      {isNew && (
        <path
          d={starburst(100 - mark, mark, mark * 1.15)}
          fill="#FFFFFF"
          stroke="hsl(var(--background))"
          strokeWidth="4"
          strokeLinejoin="round"
          paintOrder="stroke"
          className="motion-safe:animate-badge-spin-in"
          style={{ transformBox: "fill-box", transformOrigin: "center" }}
        />
      )}
    </svg>
  );
}
